import { calculateHybridRlAggregate } from "./rl-aggregate";
import {
  ACCOUNT_TEAM_AVATAR_BG,
  CUSTOMER_CONTACT_AVATAR_BG,
  EXEC_SPONSOR_AVATAR_BG,
  getAccountTeamAvatarBg,
  getCustomerContactAvatarBg,
  getExecSponsorAvatarBg,
  getProfileAvatarBg,
  getVendorProfileAvatarBg,
} from "../../../Utils/Helper/profileAvatarUi";

export {
  ACCOUNT_TEAM_AVATAR_BG,
  CUSTOMER_CONTACT_AVATAR_BG,
  EXEC_SPONSOR_AVATAR_BG,
  getAccountTeamAvatarBg,
  getCustomerContactAvatarBg,
  getExecSponsorAvatarBg,
  getProfileAvatarBg,
  getVendorProfileAvatarBg,
};

const prefer = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== "");

/** Vendor labels from relationship-assessment overview (`account_team` API bucket). */
export const VENDOR_LABEL_ACCOUNT_TEAM = "account_team";

export const VENDOR_LABELS_EXEC_TEAM = new Set([
  "executive_sponsor",
  "extra_sponsor",
  "executive",
  "c_level",
]);

export function normalizeVendorStakeholderLabel(label) {
  return String(label || "")
    .trim()
    .toLowerCase();
}

export function isAccountTeamVendorLabel(label) {
  return normalizeVendorStakeholderLabel(label) === VENDOR_LABEL_ACCOUNT_TEAM;
}

export function isExecTeamVendorLabel(label) {
  const normalized = normalizeVendorStakeholderLabel(label);
  return Boolean(normalized && VENDOR_LABELS_EXEC_TEAM.has(normalized));
}

/** Map overview `label` → relationship vendorType (`account_team_member` | `executive`). */
export function resolveOverviewVendorType(label) {
  if (isAccountTeamVendorLabel(label)) return "account_team_member";
  return "executive";
}

export function buildOverviewVendorIdSets(overview) {
  const accountTeamIds = new Set();
  const executiveIds = new Set();

  for (const item of overview?.account_team || []) {
    const id = String(item.user_id);
    if (isAccountTeamVendorLabel(item.label)) {
      accountTeamIds.add(id);
    } else {
      executiveIds.add(id);
    }
  }

  return { accountTeamIds, executiveIds };
}

export function isAccountTeamStakeholder(stakeholder) {
  const label = stakeholder?.raw?.label ?? stakeholder?.label;
  if (label) return isAccountTeamVendorLabel(label);
  return stakeholder?.vendorType === "team_member";
}

export function isAccountTeamVendor(vendor) {
  if (!vendor) return false;
  return (
    vendor.vendorType === "team_member" ||
    vendor.__vendorType === "account_team_member"
  );
}

export function resolveVendorDisplayName(vendor) {
  return vendor?.member_name || vendor?.name || "Vendor";
}

function formatExecRoleAsTitle(role) {
  const normalized = String(role || "")
    .toLowerCase()
    .replace(/-/g, "_")
    .replace(/\s+/g, "_");
  if (!normalized) return "";
  if (normalized === "c_level" || normalized === "clevel") return "C-Level";
  if (normalized === "sponsor" || normalized === "executive_sponsor") {
    return "Executive Sponsor";
  }
  return String(role).replace(/_/g, " ");
}

/** Job title for vendor row headers — matches relationship matrix / relationshipDataAdapter. */
export function resolveVendorJobTitle(vendor) {
  if (!vendor) return "";
  const raw = vendor.raw || {};
  if (isAccountTeamVendor(vendor)) {
    const memberTitle = prefer(
      vendor.member_title,
      raw.member_title,
      raw.memberTitle,
      ""
    );
    if (memberTitle) return String(memberTitle).trim();

    const roleTag = resolveVendorRoleTag(vendor);
    if (roleTag) {
      return (
        formatAccountTeamRoleDisplay(vendor, { withExpansion: true }) ||
        ACCOUNT_TEAM_ROLE_LABELS[roleTag] ||
        ""
      );
    }

    const fallbackTitle = prefer(vendor.title, raw.title, "");
    const roleCode = getAccountTeamRoleCode(vendor);
    if (
      fallbackTitle &&
      String(fallbackTitle).trim().toUpperCase() !==
        String(roleCode).toUpperCase()
    ) {
      return String(fallbackTitle).trim();
    }
    return "";
  }
  const title = String(
    prefer(
      vendor.title,
      raw.title,
      vendor.executive_title,
      raw.executive_title,
      ""
    ) || ""
  ).trim();
  if (title) return title;

  const sponsorRole = vendor.sponsorRole || vendor.type;
  if (sponsorRole === "primary") return "Primary Sponsor";
  if (sponsorRole === "secondary") return "Secondary Sponsor";
  if (sponsorRole === "additional") return "Executive Sponsor";

  return formatExecRoleAsTitle(prefer(vendor.role, raw.role, ""));
}

/** Short role code tag for account team vendors (AE, CSM, …). */
export function resolveVendorRoleTag(vendor) {
  if (!isAccountTeamVendor(vendor)) return "";
  const code = getAccountTeamRoleCode(vendor);
  return code ? code.toUpperCase() : "";
}

/**
 * Role label under vendor name on relationship map (exec sponsor / account team code).
 * Distinct from resolveVendorSubLabel (grade for execs) and resolveVendorJobTitle (job title).
 */
export function resolveVendorRoleLabel(vendor) {
  if (!vendor) return "";
  if (isAccountTeamVendor(vendor)) {
    return formatAccountTeamRoleDisplay(vendor) || "Account Team";
  }
  const raw = vendor.raw || {};
  const sponsorRole = vendor.sponsorRole || vendor.type;
  if (sponsorRole === "primary") return "Primary Sponsor";
  if (sponsorRole === "secondary") return "Secondary Sponsor";
  if (sponsorRole === "additional") return "Executive Sponsor";
  return (
    formatExecRoleAsTitle(prefer(vendor.role, raw.role, "")) ||
    "Executive Sponsor"
  );
}

/** Primary / secondary sponsor badge for exec vendors. */
export function resolveExecSponsorBadgeType(vendor) {
  const role = vendor?.sponsorRole || vendor?.type;
  if (role === "primary") return "primary";
  if (role === "secondary") return "secondary";
  return null;
}

export const RL_LEVELS = [0, 1, 2, 3, 4, 5];
export const AL_LEVELS = [5, 4, 3, 2, 1, 0];
/** Portfolio grid Y-axis: high AL at top, explicit Unknown AL row at bottom (matches prototype). */
export const AL_PORTFOLIO_ROWS = [5, 4, 3, 2, 1, 0, "unknown"];
/** Vendor grade bands 5→1 for matrix row order (matches executives.grade / ladder_level). */
export const GRADE_LEVELS = [5, 4, 3, 2, 1];
/** @deprecated Use GRADE_LEVELS — not a separate GL config table. */
export const GL_LEVELS = GRADE_LEVELS;

/**
 * Org label for numeric grade (organizations.grade_mapping.grade_N).
 * @param {number|string} grade 1–5
 * @param {Record<string, string>} [gradeMapping]
 */
export function getVendorGradeLabel(grade, gradeMapping = {}) {
  const n = Number(grade);
  if (!Number.isFinite(n) || n < 1 || n > 5) return "Ungraded";
  const key = `grade_${Math.round(n)}`;
  return gradeMapping[key] || `Grade ${Math.round(n)}`;
}

/** Numeric grade from stakeholder (executives.grade → ladderLevel in adapters). */
export function getStakeholderGradeLevel(stakeholder) {
  const raw = stakeholder?.raw || {};
  const n = Number(
    stakeholder?.ladderLevel ?? raw.grade ?? raw.ladder_level ?? raw.grade_level
  );
  return Number.isFinite(n) && n >= 1 && n <= 5 ? Math.round(n) : null;
}

/** Short codes on account_team_members.role (matches Account Details / Coverage Matrix). */
export const ACCOUNT_TEAM_ROLE_LABELS = {
  AE: "Account Executive",
  CSM: "Customer Success Manager",
  SE: "Solution Architect",
  TAM: "Technical Account Manager",
  BCS: "Business Continuity Services",
  SDR: "Sales Development Representative",
  VP: "Vice President",
};

/** Role code from stakeholder or raw account_team_members row. */
export function getAccountTeamRoleCode(stakeholderOrRow = {}) {
  const raw = stakeholderOrRow.raw || stakeholderOrRow;
  const code = String(
    prefer(
      stakeholderOrRow.role,
      raw.role,
      raw.functional_role,
      stakeholderOrRow.functional_role
    ) || ""
  ).trim();
  return code;
}

/** Display label for matrix / coverage subtitles (e.g. SDR, AE). */
export function formatAccountTeamRoleDisplay(
  stakeholderOrRow = {},
  { withExpansion = false } = {}
) {
  const code = getAccountTeamRoleCode(stakeholderOrRow);
  if (!code) {
    return prefer(
      stakeholderOrRow.member_title,
      stakeholderOrRow.raw?.member_title,
      stakeholderOrRow.title,
      ""
    );
  }
  const upper = code.toUpperCase();
  if (!withExpansion) return upper;
  const full = ACCOUNT_TEAM_ROLE_LABELS[upper];
  return full ? `${upper} (${full})` : code;
}

/**
 * Subtitle under vendor name — org grade label for execs, functional role for account team.
 * Matches RelationshipsMatrixTable VendorRowHeader.
 */
export function resolveVendorSubLabel(
  stakeholder,
  gradeMapping = {},
  getGradeLabel
) {
  if (!stakeholder) return "";
  if (isAccountTeamStakeholder(stakeholder)) {
    return formatAccountTeamRoleDisplay(stakeholder) || "Account Team";
  }
  const grade = getStakeholderGradeLevel(stakeholder);
  if (grade === null) return stakeholder.title || "";
  return (
    getVendorGradeLabel(grade, gradeMapping) || getGradeLabel?.(grade) || ""
  );
}

export const MEDDPICC_ROLES = [
  "Metrics",
  "Economic Buyer",
  "Decision Criteria",
  "Decision Process",
  "Identified Pain",
  "Champion",
  "Competition",
];

/** account_contacts.meddpicc_role (snake_case) → matrix column label (MEDDPICC_ROLES). */
const CANONICAL_MEDDPICC_TO_COLUMN = {
  metrics: "Metrics",
  economic_buyer: "Economic Buyer",
  champion: "Champion",
  decision_maker: "Decision Criteria",
  decision_criteria: "Decision Criteria",
  decision_process: "Decision Process",
  identified_pain: "Identified Pain",
  competition: "Competition",
  influencer: "Champion",
  user: "Identified Pain",
  coach: "Decision Process",
  technical_buyer: "Decision Criteria",
  paper_process: "Decision Process",
  paper_processor: "Decision Process",
  unknown: "Champion",
};

const normalizeMeddpiccToken = role =>
  String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

function isUnknownMeddpiccToken(token) {
  return [
    "",
    "na",
    "n_a",
    "n_a_",
    "not_available",
    "not_applicable",
    "none",
    "null",
    "unknown",
  ].includes(token);
}

/**
 * Map DB/API meddpicc role to a matrix column key used by MEDDPICC_ROLES.
 */
export function meddpiccRoleToMatrixColumn(role) {
  const token = normalizeMeddpiccToken(role);
  if (isUnknownMeddpiccToken(token)) return MEDD_NOT_APPLICABLE_LABEL;
  if (CANONICAL_TO_MEDD_KEY[token]) {
    return (
      MEDD_MATRIX_ROLE_DEFS[CANONICAL_TO_MEDD_KEY[token]]?.label ||
      MEDD_NOT_APPLICABLE_LABEL
    );
  }
  if (CANONICAL_MEDDPICC_TO_COLUMN[token]) {
    return CANONICAL_MEDDPICC_TO_COLUMN[token];
  }

  const direct = MEDDPICC_ROLES.find(
    label =>
      normalizeMeddpiccToken(label) === token ||
      label.toLowerCase() ===
        String(role || "")
          .trim()
          .toLowerCase()
  );
  if (direct) return direct;

  const matrixMatch = Object.entries(MEDD_MATRIX_ROLE_DEFS).find(
    ([, def]) =>
      normalizeMeddpiccToken(def.label) === token ||
      def.label.toLowerCase() ===
        String(role || "")
          .trim()
          .toLowerCase()
  );
  if (matrixMatch) return matrixMatch[1].label;

  return MEDD_NOT_APPLICABLE_LABEL;
}

export function groupContactsByMeddpiccColumn(contacts) {
  return (contacts || []).reduce((acc, contact) => {
    const column = meddpiccRoleToMatrixColumn(
      contact.meddpiccRole ||
        contact.meddpiccRoleCanonical ||
        contact.raw?.meddpicc_role
    );
    if (!acc[column]) acc[column] = [];
    acc[column].push(contact);
    return acc;
  }, {});
}

/** Prototype matrix: fixed MEDDPICC columns (8 slots). */
export const MEDD_MATRIX_ROLE_DEFS = {
  EB: {
    label: "Economic Buyer",
    group: "buying",
    desc: "Controls budget and can unilaterally approve the deal. The ultimate yes/no on spend.",
  },
  DM: {
    label: "Decision Maker",
    group: "buying",
    desc: "Makes or ratifies the final vendor selection. May be the EB or a separate executive.",
  },
  CH: {
    label: "Champion",
    group: "buying",
    desc: "Sells on your behalf internally. Without a strong champion, deals stall or drift.",
  },
  EX: {
    label: "Executive",
    group: "executives",
    desc: "General executive or senior leader.",
  },
  IN: {
    label: "Influencer",
    group: "influence",
    desc: "Shapes opinion across the buying group without owning the final decision.",
  },
  TB: {
    label: "Technical Buyer",
    group: "influence",
    desc: "Evaluates technical fit and can block or approve on technical grounds.",
  },
  PR: {
    label: "Procurement",
    group: "influence",
    desc: "Owns the paper process — contracts, legal review, and vendor onboarding. Maps to P in MEDDPICC.",
  },
  IT: {
    label: "IT Stakeholder",
    group: "adoption",
    desc: "Implementation gatekeeper. Manages integration and rollout. Critical for on-time delivery.",
  },
  EU: {
    label: "End User",
    group: "adoption",
    desc: "Uses the product day-to-day. Influences adoption quality, renewal, and expansion.",
  },
  UN: { label: "Not Applicable", group: "unknown" },
};

export const MEDD_NOT_APPLICABLE_LABEL = "Not Applicable";

/** Display MEDDPICC key badge text (UN → "-"). */
export function formatMeddKeyDisplay(key) {
  if (!key || key === "UN" || key === "NA") return "-";
  return key;
}

/** Display MEDDPICC role label (Unknown → Not Applicable). */
export function formatMeddRoleLabelDisplay(label) {
  if (!label || label === "Unknown") return MEDD_NOT_APPLICABLE_LABEL;
  return label;
}

/** Display MEDDPICC group header label (Unknown → Not Applicable). */
export function formatMeddGroupLabelDisplay(label) {
  return formatMeddRoleLabelDisplay(label);
}

/** Compact MEDDPICC subtitle for contact lists / network map (Not Applicable → "-"). */
export function formatMeddContactSubtitleDisplay(label) {
  const trimmed = String(label || "").trim();
  if (!trimmed) return "";
  if (formatMeddRoleLabelDisplay(trimmed) === MEDD_NOT_APPLICABLE_LABEL) {
    return "-";
  }
  return trimmed;
}

export const MEDD_MATRIX_KEY_ORDER = [
  "EB",
  "DM",
  "CH",
  "EX",
  "IN",
  "TB",
  "PR",
  "IT",
  "EU",
  "UN",
];

export const MEDD_MATRIX_GROUP_ORDER = [
  "buying",
  "executives",
  "influence",
  "adoption",
  "unknown",
];

export const MEDD_MATRIX_GROUP_DEFS = {
  buying: { label: "Buying Committee", span: 3 },
  executives: { label: "Executives", span: 1 },
  influence: { label: "Influence Network", span: 3 },
  adoption: { label: "Adoption Circle", span: 2 },
  unknown: { label: "Not Applicable", span: 1 },
};

const CANONICAL_TO_MEDD_KEY = {
  eb: "EB",
  economic_buyer: "EB",
  dm: "DM",
  decision_maker: "DM",
  ch: "CH",
  champion: "CH",
  ex: "EX",
  executive: "EX",
  in: "IN",
  influencer: "IN",
  tb: "TB",
  technical_buyer: "TB",
  pr: "PR",
  procurement: "PR",
  coach: "PR",
  paper_process: "PR",
  paper_processor: "PR",
  it: "IT",
  it_stakeholder: "IT",
  eu: "EU",
  user: "EU",
  end_user: "EU",
  identified_pain: "EU",
  metrics: "IN",
  competition: "IN",
  unknown: "UN",
};

export const MEDD_AVATAR_BG = {
  EB: "#b45309",
  DM: "#4f46e5",
  CH: "#059669",
  EX: "#4f46e5",
  IN: "#6b7280",
  TB: "#1e40af",
  PR: "#065f46",
  IT: "#6b21a8",
  EU: "#9d174d",
  UN: "#64748b",
};

/**
 * Single source of truth for per-contact MEDDPICC key, display label, and matrix group.
 * Used by buildPerContactMatrixColumns, ring full view, and coverage/customer-team UIs.
 */
export function resolveContactMeddpiccMatrixMeta(contact) {
  const raw = readCustomerMeddpiccRole(contact);
  const meddKey = meddpiccCanonicalToMatrixKey(raw);
  const role = MEDD_MATRIX_ROLE_DEFS[meddKey] || {
    label: "Contact",
    group: "unknown",
  };
  const roleLabel = formatMeddpiccRoleLabel(raw, role.label);
  return {
    meddKey,
    meddRoleLabel: roleLabel,
    meddGroup: role.group,
  };
}

/** Ring full-view legend: "EB Economic Buyer · DM Decision Maker · CH Champion · EX Executive · …" */
export function buildMeddpiccRingsLegendText() {
  return MEDD_MATRIX_KEY_ORDER.map(key => {
    const label = formatMeddRoleLabelDisplay(
      MEDD_MATRIX_ROLE_DEFS[key]?.label || key
    );
    return `${formatMeddKeyDisplay(key)} ${label}`;
  }).join(" · ");
}

/** Matrix bulb colors — RL 4–5 share strong (green) per matrix legend. */
export const RL_DOT_COLORS = {
  0: "#94a3b8",
  1: "#ef4444",
  2: "#f97316",
  3: "#eab308",
  4: "#22c55e",
  5: "#22c55e",
};

export const STRONG_RL_MIN = 4;

export function isStrongRlLevel(rl) {
  const level = clampLevel(rl);
  return level >= STRONG_RL_MIN;
}

/** Canonical posture values (DB + UI). */
export const ASSESSED_POSTURE_VALUES = [
  "advocate",
  "neutral",
  "detractor",
  "unknown",
];

const LEGACY_POSTURE_TO_CANONICAL = {
  champion: "advocate",
  supporter: "advocate",
  blocker: "detractor",
  neutral: "neutral",
  unknown: "unknown",
};

const POSTURE_LETTER = {
  Advocate: "A",
  Neutral: "N",
  Detractor: "D",
  Unknown: "U",
  advocate: "A",
  neutral: "N",
  detractor: "D",
  unknown: "U",
};

/** Normalize to advocate / neutral / detractor / unknown (accepts legacy champion/blocker). */
export function normalizePosture(value) {
  const key = String(value || "unknown")
    .trim()
    .toLowerCase();
  if (LEGACY_POSTURE_TO_CANONICAL[key]) return LEGACY_POSTURE_TO_CANONICAL[key];
  if (ASSESSED_POSTURE_VALUES.includes(key)) return key;
  return "unknown";
}

/** Title-case label for UI tooltips and display (Advocate, Neutral, Detractor, Unknown). */
export function formatPostureLabel(value) {
  const normalized = normalizePosture(value);
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export const GOAL_HORIZON_MIN = 1;
export const GOAL_HORIZON_MAX = 36;

/** @deprecated Legacy dropdown options; goal target months are now free-form 1–36. */
export const GOAL_HORIZON_OPTIONS = [
  { value: "", label: "No date" },
  { value: "1m", label: "1 month" },
  { value: "2m", label: "2 months" },
  { value: "3m", label: "3 months" },
  { value: "6m", label: "6 months" },
  { value: "12m", label: "12 months" },
];

function monthsFromIsoDate(targetDate, referenceDate = new Date()) {
  const target = new Date(String(targetDate).trim());
  const ref = referenceDate ? new Date(referenceDate) : new Date();
  if (Number.isNaN(target.getTime()) || Number.isNaN(ref.getTime()))
    return null;

  const diffMs = target.getTime() - ref.getTime();
  if (diffMs <= 0) return null;

  const months = Math.max(
    1,
    Math.round(diffMs / (1000 * 60 * 60 * 24 * 30.4375))
  );
  return Math.min(GOAL_HORIZON_MAX, months);
}

/** Normalize API/form target months from number, `Nm`, ISO date, or plain string. */
export function normalizeGoalTargetMonths(value, referenceDate) {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "number" && Number.isFinite(value)) {
    const rounded = Math.round(value);
    if (rounded >= GOAL_HORIZON_MIN && rounded <= GOAL_HORIZON_MAX)
      return rounded;
    return null;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d+$/.test(raw)) {
    const rounded = Number(raw);
    if (rounded >= GOAL_HORIZON_MIN && rounded <= GOAL_HORIZON_MAX)
      return rounded;
    return null;
  }

  const monthMatch = raw.match(/^(\d+)m$/i);
  if (monthMatch) {
    const rounded = Number(monthMatch[1]);
    if (rounded >= GOAL_HORIZON_MIN && rounded <= GOAL_HORIZON_MAX)
      return rounded;
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return monthsFromIsoDate(raw, referenceDate);
  }

  return null;
}

export function goalTargetMonthsToInputValue(value, referenceDate) {
  const months = normalizeGoalTargetMonths(value, referenceDate);
  return months === null ? "" : String(months);
}

/** @deprecated Use normalizeGoalTargetMonths — backend now expects month count as a number. */
export function goalHorizonValueToTargetDate(horizon) {
  return normalizeGoalTargetMonths(horizon);
}

/** @deprecated Use normalizeGoalTargetMonths. */
export function targetDateToHorizonValue(
  targetDate,
  referenceDate = new Date()
) {
  const months = normalizeGoalTargetMonths(targetDate, referenceDate);
  return months === null ? "" : String(months);
}

export function formatGoalHorizonLabel(horizon, referenceDate) {
  const months = normalizeGoalTargetMonths(horizon, referenceDate);
  if (months === null) return "";
  return `${months} month${months === 1 ? "" : "s"}`;
}

/** Stored goal target when target is at or above current assessed RL. */
export function getPairGoalTargetRl(pair) {
  const raw = pair?.goalTargetRl ?? pair?.targetRlScore;
  if (raw === null || raw === undefined) return null;
  const target = clampLevel(raw);
  const current =
    pair?.rlLevel !== null && pair?.rlLevel !== undefined
      ? clampLevel(pair.rlLevel)
      : null;
  if (current === null || target < current) return null;
  return target;
}

/** Prefer pair-level assessed posture; fall back to contact record. Returns display label. */
export function resolvePairDisplayPosture(pair, contact) {
  if (pair?.assessedPosture) return formatPostureLabel(pair.assessedPosture);
  if (pair?.raw?.assessed_posture) {
    return formatPostureLabel(pair.raw.assessed_posture);
  }
  if (contact?.posture) return formatPostureLabel(contact.posture);
  return "Unknown";
}

export function formatContactDisplayName(row = {}) {
  const fromParts = [row.first_name, row.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return (
    row.name ||
    fromParts ||
    row.full_name ||
    row.contact_name ||
    row.member_name ||
    "Unknown Contact"
  );
}

export function meddpiccCanonicalToMatrixKey(role) {
  const code = String(role || "")
    .trim()
    .toUpperCase();
  if (code === "NA") return "UN";
  if (MEDD_MATRIX_ROLE_DEFS[code]) return code;

  const token = normalizeMeddpiccToken(role);
  if (isUnknownMeddpiccToken(token)) return "UN";
  if (CANONICAL_TO_MEDD_KEY[token]) return CANONICAL_TO_MEDD_KEY[token];
  const column = meddpiccRoleToMatrixColumn(role);
  const byLabel = Object.entries(MEDD_MATRIX_ROLE_DEFS).find(
    ([, def]) => def.label === column
  );
  return byLabel ? byLabel[0] : "UN";
}

export function readCustomerMeddpiccRole(item) {
  return (
    item?.meddpiccRoleCanonical ||
    item?.meddpicc_role ||
    item?.raw?.meddpicc_role ||
    item?.meddpiccRole ||
    item?.role ||
    null
  );
}

/** Job title for customer team member / account contact (not MEDDPICC role). */
export function resolveCustomerMemberTitle(item) {
  if (!item) return "";
  const title = String(
    item.member_title ?? item.title ?? item.position ?? ""
  ).trim();
  return title;
}

/** Display label for a customer team member column (matrix / coverage / sidebar). */
export function resolveCustomerMeddRoleLabel(item) {
  const key = meddpiccCanonicalToMatrixKey(readCustomerMeddpiccRole(item));
  return MEDD_MATRIX_ROLE_DEFS[key]?.label || "";
}

function customerStakeholderDisplayName(item) {
  return item?.member_name || item?.name || "";
}

/**
 * Customer contacts / customer_team_members column order.
 * MEDDPICC on: Buying → Influence → Adoption → Not Applicable, then name A→Z.
 * MEDDPICC off: name A→Z only.
 */
export function sortCustomerStakeholdersForDisplay(
  items,
  { considerMeddpiccRole = true } = {}
) {
  const list = [...(items || [])];
  if (!considerMeddpiccRole) {
    return list.sort((a, b) =>
      String(customerStakeholderDisplayName(a)).localeCompare(
        String(customerStakeholderDisplayName(b)),
        undefined,
        { sensitivity: "base" }
      )
    );
  }
  const groupRank = {
    buying: 0,
    executives: 1,
    influence: 2,
    adoption: 3,
    unknown: 4,
  };
  return list.sort((a, b) => {
    const keyA = meddpiccCanonicalToMatrixKey(readCustomerMeddpiccRole(a));
    const keyB = meddpiccCanonicalToMatrixKey(readCustomerMeddpiccRole(b));
    const rankA = groupRank[MEDD_MATRIX_ROLE_DEFS[keyA]?.group] ?? 4;
    const rankB = groupRank[MEDD_MATRIX_ROLE_DEFS[keyB]?.group] ?? 4;
    if (rankA !== rankB) return rankA - rankB;
    return String(customerStakeholderDisplayName(a)).localeCompare(
      String(customerStakeholderDisplayName(b)),
      undefined,
      { sensitivity: "base" }
    );
  });
}

/** Sidebar/list grouping when MEDDPICC is enabled. */
export function groupCustomerStakeholdersByMeddpicc(
  items,
  { considerMeddpiccRole = true } = {}
) {
  const sorted = sortCustomerStakeholdersForDisplay(items, {
    considerMeddpiccRole,
  });
  if (!considerMeddpiccRole) {
    return [{ group: null, label: null, items: sorted }];
  }
  const sections = [];
  let currentGroup = null;
  let bucket = [];
  const flush = () => {
    if (!bucket.length) return;
    sections.push({
      group: currentGroup,
      label:
        MEDD_MATRIX_GROUP_DEFS[currentGroup]?.label ||
        MEDD_NOT_APPLICABLE_LABEL,
      items: bucket,
    });
    bucket = [];
  };
  for (const item of sorted) {
    const key = meddpiccCanonicalToMatrixKey(readCustomerMeddpiccRole(item));
    const grp = MEDD_MATRIX_ROLE_DEFS[key]?.group || "unknown";
    if (grp !== currentGroup) {
      flush();
      currentGroup = grp;
    }
    bucket.push(item);
  }
  flush();
  return sections;
}

/** MEDDPICC group header spans for customer_team_members columns (coverage / Account Details matrix). */
export function buildCustomerTeamMeddGroupSpans(
  members,
  { considerMeddpiccRole = true } = {}
) {
  if (!considerMeddpiccRole) return [];
  const columnDefs = sortCustomerStakeholdersForDisplay(members, {
    considerMeddpiccRole,
  }).map(member => {
    const key = meddpiccCanonicalToMatrixKey(readCustomerMeddpiccRole(member));
    return {
      group: MEDD_MATRIX_ROLE_DEFS[key]?.group || "unknown",
      isGhost: false,
    };
  });
  return buildMeddGroupHeaderSpans(columnDefs);
}

/**
 * Relationships matrix X-axis: one column per customer contact (vendor × contact pairs).
 * MEDDPICC group headers span only columns that exist (no ghost slots).
 */
export function buildPerContactMatrixColumns(
  contacts,
  { considerMeddpiccRole = true } = {}
) {
  const list = sortCustomerStakeholdersForDisplay(contacts, {
    considerMeddpiccRole,
  });

  return list.map(contact => {
    const { meddKey, meddRoleLabel, meddGroup } =
      resolveContactMeddpiccMatrixMeta(contact);
    return {
      key: `contact-${contact.id}`,
      meddKey,
      label: meddRoleLabel,
      group: meddGroup,
      contact,
      contacts: [contact],
      isGhost: false,
      layout: "per-contact",
    };
  });
}

/** Colspan per MEDDPICC section for the group header row. */
export function buildMeddGroupHeaderSpans(columnDefs) {
  const counts = { buying: 0, executives: 0, influence: 0, adoption: 0 };
  for (const col of columnDefs || []) {
    if (col.isGhost) continue;
    counts[col.group] = (counts[col.group] || 0) + 1;
  }
  return MEDD_MATRIX_GROUP_ORDER.filter(grp => counts[grp] > 0).map(grp => ({
    group: grp,
    span: counts[grp],
    label: MEDD_MATRIX_GROUP_DEFS[grp].label,
  }));
}

function formatMeddpiccRoleLabel(canonical, fallback) {
  const token = String(canonical || "").trim();
  if (!token) return formatMeddRoleLabelDisplay(fallback);
  const meddKey = meddpiccCanonicalToMatrixKey(canonical);
  if (meddKey === "UN") return MEDD_NOT_APPLICABLE_LABEL;
  return MEDD_MATRIX_ROLE_DEFS[meddKey]?.label || fallback;
}

/** One column per MEDDPICC slot — real contact(s) or ghost (8-slot prototype layout). */
export function buildMatrixColumnDefs(contacts) {
  const byKey = {};
  for (const contact of contacts || []) {
    const key = meddpiccCanonicalToMatrixKey(readCustomerMeddpiccRole(contact));
    if (!byKey[key]) byKey[key] = [];
    byKey[key].push(contact);
  }
  return MEDD_MATRIX_KEY_ORDER.map(key => {
    const role = MEDD_MATRIX_ROLE_DEFS[key];
    const columnContacts = byKey[key] || [];
    const contact = columnContacts[0] || null;
    return {
      key,
      label: role.label,
      group: role.group,
      contact,
      contacts: columnContacts,
      isGhost: columnContacts.length === 0,
    };
  });
}

/** True when pair contact id matches matrix contact (incl. legacy CTM bridge). */
export function pairMatchesContact(pair, contact) {
  if (!pair || !contact) return false;
  if (String(pair.contactId) === String(contact.id)) return true;
  const legacyCtm = contact.raw?.legacy_customer_team_member_id;
  const pairLegacy =
    pair.raw?.legacy_customer_team_member_id ||
    pair.raw?.legacyCustomerTeamMemberId;
  if (legacyCtm && pairLegacy && String(legacyCtm) === String(pairLegacy))
    return true;
  return false;
}

/** Resolve account_contacts row for a pair (Tab 3 + matrix parity). */
export function resolveContactForPair(pair, contacts) {
  if (!pair || !contacts?.length) return null;
  for (const contact of contacts) {
    if (pairMatchesContact(pair, contact)) return contact;
  }
  const rawContactId =
    pair.raw?.account_contact_id || pair.raw?.accountContactId;
  const contactId = prefer(pair.contactId, rawContactId);
  if (contactId) {
    return contacts.find(c => String(c.id) === String(contactId)) || null;
  }
  return null;
}

/** All matrix row ids a pair can appear on (team + exec when both are stored). */
export function getPairStakeholderIds(pair) {
  if (!pair) return [];
  const raw = pair.raw || {};
  const ids = new Set();
  if (pair.vendorStakeholderId) ids.add(pair.vendorStakeholderId);
  const teamId =
    raw.account_team_member_id ||
    raw.accountTeamMemberId ||
    (pair.vendorType === "team_member" ? pair.vendorSourceId : null);
  const execId =
    raw.executive_id ||
    raw.executiveId ||
    (pair.vendorType === "executive" ? pair.vendorSourceId : null);
  if (teamId) ids.add(`team-${teamId}`);
  if (execId) ids.add(`exec-${execId}`);
  return [...ids];
}

/** Match pair vendor row to matrix stakeholder id (`exec-*` / `team-*`). */
export function pairMatchesStakeholder(pair, stakeholderId) {
  if (!pair || !stakeholderId) return false;
  return getPairStakeholderIds(pair).includes(stakeholderId);
}

/** @deprecated Prefer buildPerContactMatrixColumns for Relationships matrix. */
export function buildMatrixColumnDefsForAccount(contacts) {
  return buildPerContactMatrixColumns(contacts);
}

function compareVendorDisplayName(a, b) {
  const nameA = a?.name || a?.member_name || "";
  const nameB = b?.name || b?.member_name || "";
  return String(nameA).localeCompare(String(nameB), undefined, {
    sensitivity: "base",
  });
}

function readVendorLadderLevel(item) {
  return (
    Number(
      item?.ladderLevel ??
        item?.ladder_level ??
        item?.grade ??
        item?.raw?.ladder_level ??
        item?.raw?.grade ??
        0
    ) || 0
  );
}

/** Higher ladder/grade first; tie-break by name (A→Z). */
export function compareVendorLadderDesc(a, b) {
  const ladderDelta = readVendorLadderLevel(b) - readVendorLadderLevel(a);
  if (ladderDelta !== 0) return ladderDelta;
  return compareVendorDisplayName(a, b);
}

export function execSponsorSortRank(item) {
  const type = item?.sponsorRole || item?.type;
  if (type === "primary") return 0;
  if (type === "secondary") return 1;
  if (type === "additional") return 2;
  return 3;
}

/** Exec Team: Primary → Secondary → Additional → ladder high→low → name A→Z. */
export function sortExecSponsorsForDisplay(sponsors) {
  return [...(sponsors || [])].sort((a, b) => {
    const roleDelta = execSponsorSortRank(a) - execSponsorSortRank(b);
    if (roleDelta !== 0) return roleDelta;
    return compareVendorLadderDesc(a, b);
  });
}

/** Account Team: ladder high→low → name A→Z. */
export function sortAccountTeamMembersForDisplay(members) {
  return [...(members || [])].sort(compareVendorLadderDesc);
}

export function sortExecStakeholdersForDisplay(stakeholders) {
  return [...(stakeholders || [])].sort((a, b) => {
    const roleDelta = execSponsorSortRank(a) - execSponsorSortRank(b);
    if (roleDelta !== 0) return roleDelta;
    return compareVendorLadderDesc(a, b);
  });
}

export function sortAccountTeamStakeholdersForDisplay(stakeholders) {
  return [...(stakeholders || [])].sort(compareVendorLadderDesc);
}

export function splitStakeholdersForMatrix(stakeholders) {
  const execTeam = [];
  const accountTeam = [];
  for (const stakeholder of stakeholders || []) {
    if (isAccountTeamStakeholder(stakeholder)) {
      accountTeam.push(stakeholder);
    } else {
      execTeam.push(stakeholder);
    }
  }
  return {
    execTeam: sortExecStakeholdersForDisplay(execTeam),
    accountTeam: sortAccountTeamStakeholdersForDisplay(accountTeam),
  };
}

export function getPostureLetter(posture) {
  return POSTURE_LETTER[posture] || POSTURE_LETTER.Unknown;
}

export const levelPalette = {
  0: {
    bg: "bg-slate-100",
    text: "text-slate-700",
    border: "border-slate-300",
    dot: "bg-slate-400",
  },
  1: {
    bg: "bg-red-100",
    text: "text-red-800",
    border: "border-red-300",
    dot: "bg-red-500",
  },
  2: {
    bg: "bg-orange-100",
    text: "text-orange-800",
    border: "border-orange-300",
    dot: "bg-orange-500",
  },
  3: {
    bg: "bg-yellow-100",
    text: "text-yellow-800",
    border: "border-yellow-300",
    dot: "bg-yellow-500",
  },
  4: {
    bg: "bg-green-100",
    text: "text-green-800",
    border: "border-green-300",
    dot: "bg-green-500",
  },
  5: {
    bg: "bg-green-100",
    text: "text-green-800",
    border: "border-green-300",
    dot: "bg-green-500",
  },
};

export const posturePalette = {
  Advocate: "bg-green-50 text-green-700 border-green-200",
  Neutral: "bg-slate-50 text-slate-700 border-slate-200",
  Detractor: "bg-red-50 text-red-700 border-red-200",
  Unknown: "bg-gray-50 text-gray-600 border-gray-200",
};

export const getInitials = (name = "") => {
  const initials = String(name)
    .split(/\s+/)
    .filter(Boolean)
    .map(part => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return initials || "NA";
};

export const clampLevel = (value, fallback = 0) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, Math.min(5, Math.round(numeric)));
};

export const MAPPED_UNASSESSED_DOT_COLOR = "#94a3b8";

/** Coverage matrix modal: dot color by RL band (matches matrix legend). */
export function getCoverageMatrixDotColor(rl) {
  if (rl === null || rl === undefined) return MAPPED_UNASSESSED_DOT_COLOR;
  const level = clampLevel(rl);
  if (level >= STRONG_RL_MIN) return RL_DOT_COLORS[4];
  if (level >= 2) return "#eab308";
  return RL_DOT_COLORS[1];
}

/** Solid pill style for RL band badges (green / yellow / red / grey). */
export function getRlBandPillStyle(rl) {
  const background =
    rl === null || rl === undefined
      ? MAPPED_UNASSESSED_DOT_COLOR
      : getCoverageMatrixDotColor(rl);
  return { background, color: "#fff", borderColor: background };
}

/**
 * Account identity avatars — cool hues only (no green/yellow/red/orange).
 * Excludes CUSTOMER_CONTACT_AVATAR_BG and ACCOUNT_TEAM_AVATAR_BG / EXEC_SPONSOR_AVATAR_BG.
 */
export const ACCOUNT_IDENTITY_COLORS = [
  "#7c3aed",
  "#4f46e5",
  "#2563eb",
  "#0891b2",
  "#6366f1",
  "#8b5cf6",
  "#db2777",
  "#9333ea",
  "#0ea5e9",
  "#a855f7",
];

export const ACCOUNT_IDENTITY_FALLBACK = "#7c3aed";

function hashStringToIndex(value, modulo) {
  let hash = 0;
  for (const char of String(value || "")) {
    hash = (hash << 5) - hash + char.charCodeAt(0);
  }
  return Math.abs(hash) % modulo;
}

/** Stable account avatar color from account id (same id → same color everywhere). */
export function getAccountIdentityColor(accountId) {
  if (accountId === null || accountId === undefined || accountId === "") {
    return ACCOUNT_IDENTITY_FALLBACK;
  }
  return ACCOUNT_IDENTITY_COLORS[
    hashStringToIndex(accountId, ACCOUNT_IDENTITY_COLORS.length)
  ];
}

/**
 * Outlined initials badge — white fill, account-color border and text.
 * @returns {{ background: string, border: string, color: string, boxSizing: "border-box" }}
 */
export function getAccountIdentityBadgeStyle(accountId, options = {}) {
  const color = options.color ?? getAccountIdentityColor(accountId);
  const borderWidth = options.borderWidth ?? 1.5;
  return {
    background: "#fff",
    border: `${borderWidth}px solid ${color}`,
    color,
    boxSizing: /** @type {"border-box"} */ ("border-box"),
  };
}

function normalizeCoverageName(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/**
 * Merge account_contacts.meddpicc_role onto customer_team_members rows.
 * Coverage / Account Details use CTM ids; the relationship matrix uses account_contacts.
 */
export function enrichCustomerTeamWithMeddpicc(customerTeam, contacts) {
  const contactIdByCtm = buildContactIdByCustomerTeamMember(
    contacts,
    customerTeam
  );
  const contactById = new Map(
    (contacts || []).map(contact => [String(contact.id), contact])
  );

  return (customerTeam || []).map(member => {
    const accountContactId = contactIdByCtm.get(String(member.id));
    const contact = accountContactId ? contactById.get(accountContactId) : null;
    const memberTitle = resolveCustomerMemberTitle(contact || member);

    if (!contact) {
      if (!memberTitle) return member;
      return {
        ...member,
        member_title: memberTitle,
        title: memberTitle,
      };
    }

    const meddpiccCanonical = prefer(
      contact.meddpiccRoleCanonical,
      contact.raw?.meddpicc_role,
      contact.meddpicc_role,
      contact.meddpiccRole,
      member.role
    );

    return {
      ...member,
      meddpicc_role: meddpiccCanonical || member.meddpicc_role,
      meddpiccRoleCanonical: meddpiccCanonical || member.meddpiccRoleCanonical,
      ...(memberTitle ? { member_title: memberTitle, title: memberTitle } : {}),
    };
  });
}

/** Map customer_team_members.id → account_contacts.id (relationship matrix scope). */
export function buildContactIdByCustomerTeamMember(contacts, customerTeam) {
  const map = new Map();
  const contactByName = new Map();
  for (const contact of contacts || []) {
    const legacy =
      contact.raw?.legacy_customer_team_member_id ||
      contact.raw?.legacyCustomerTeamMemberId;
    if (legacy) map.set(String(legacy), String(contact.id));
    const nameKey = normalizeCoverageName(
      contact.name ||
        contact.member_name ||
        [contact.first_name, contact.last_name].filter(Boolean).join(" ")
    );
    if (nameKey && !contactByName.has(nameKey)) {
      contactByName.set(nameKey, String(contact.id));
    }
  }
  for (const ct of customerTeam || []) {
    const ctmId = String(ct.id);
    if (map.has(ctmId)) continue;
    const nameKey = normalizeCoverageName(ct.member_name || ct.name);
    const accountContactId = nameKey ? contactByName.get(nameKey) : null;
    if (accountContactId) map.set(ctmId, accountContactId);
  }
  return map;
}

function pairMatchesCoverageContact(
  pair,
  customerTeamMemberId,
  contactIdByCtm
) {
  const ctmId = String(customerTeamMemberId);
  const accountContactId = contactIdByCtm?.get(ctmId);
  if (accountContactId && String(pair.contactId) === String(accountContactId))
    return true;
  const legacyCtm =
    pair.raw?.legacy_customer_team_member_id ||
    pair.raw?.legacyCustomerTeamMemberId;
  return legacyCtm && String(legacyCtm) === ctmId;
}

function pairMatchesCoverageVendor(
  pair,
  vendorType,
  vendorId,
  matchExecutiveId
) {
  const raw = pair.raw || {};
  const vid = String(vendorId);
  if (vendorType === "account_team_member") {
    const teamId =
      raw.account_team_member_id ||
      raw.accountTeamMemberId ||
      (pair.vendorType === "team_member" ? pair.vendorSourceId : null);
    return teamId && String(teamId) === vid;
  }
  const execId =
    raw.executive_id ||
    raw.executiveId ||
    (pair.vendorType === "executive" ? pair.vendorSourceId : null);
  if (!execId) return false;
  if (vendorType === "executive") {
    return (
      String(execId) === vid ||
      (matchExecutiveId && String(execId) === String(matchExecutiveId))
    );
  }
  if (vendorType === "account_sponsor") {
    return matchExecutiveId && String(execId) === String(matchExecutiveId);
  }
  return false;
}

/**
 * Find assessed pair for coverage grid cell.
 * @param customerTeamMemberId — column id from /accounts/:id/teams customerTeam (not account_contacts.id)
 * @param contactIdByCtm — from buildContactIdByCustomerTeamMember(relationship contacts)
 * @param matchExecutiveId — required for account_sponsor rows (pairs store executive_id)
 */
export function findPairForCoverageCell(
  pairs,
  vendorType,
  vendorId,
  customerTeamMemberId,
  contactIdByCtm,
  matchExecutiveId = null
) {
  return (
    (pairs || []).find(
      p =>
        pairMatchesCoverageContact(p, customerTeamMemberId, contactIdByCtm) &&
        pairMatchesCoverageVendor(p, vendorType, vendorId, matchExecutiveId)
    ) || null
  );
}

/** Footer stats for stakeholder mapping matrix (mapped cells by RL band). */
export function computeStakeholderMatrixStats(
  vendorIds,
  customerIds,
  mappedKeys,
  rlByKey = new Map()
) {
  const stats = {
    strong: 0,
    moderate: 0,
    weak: 0,
    unassessed: 0,
    notMapped: 0,
  };
  for (const vendorId of vendorIds) {
    for (const customerId of customerIds) {
      const key = `${vendorId}::${customerId}`;
      if (!mappedKeys.has(key)) {
        stats.notMapped += 1;
        continue;
      }
      const rl = rlByKey.get(key);
      if (rl === null || rl === undefined) {
        stats.unassessed += 1;
        continue;
      }
      const level = clampLevel(rl);
      if (level >= STRONG_RL_MIN) stats.strong += 1;
      else if (level >= 2) stats.moderate += 1;
      else stats.weak += 1;
    }
  }
  return stats;
}

/** Footer stats for coverage modal (checked cells by RL band; unchecked = not mapped). */
export function computeCoverageMatrixStats(
  linkMap,
  allowedKeys,
  pairs,
  contactIdByCtm,
  sponsorExecByVendorKey
) {
  const stats = {
    strong: 0,
    moderate: 0,
    weak: 0,
    unassessed: 0,
    notMapped: 0,
  };
  for (const key of allowedKeys) {
    const parts = String(key).split(":");
    const vendorType = parts[0];
    const vendorId = parts[1];
    const customerId = parts.slice(2).join(":");
    const checked = !!linkMap[key];
    if (!checked) {
      stats.notMapped += 1;
      continue;
    }
    const matchExecutiveId =
      sponsorExecByVendorKey?.get(`${vendorType}:${vendorId}`) || null;
    const pair = findPairForCoverageCell(
      pairs,
      vendorType,
      vendorId,
      customerId,
      contactIdByCtm,
      matchExecutiveId
    );
    const rl = pair?.rlLevel;
    if (rl === null || rl === undefined) {
      stats.unassessed += 1;
      continue;
    }
    const level = clampLevel(rl);
    if (level >= STRONG_RL_MIN) stats.strong += 1;
    else if (level >= 2) stats.moderate += 1;
    else stats.weak += 1;
  }
  return stats;
}

/**
 * Values stored in accounts.alignment_level that mean “not set” (Unknown AL row), not AL0.
 * Includes `-` used in Postgres seed / legacy data per product convention.
 */
export function isUnpublishedAlignmentToken(raw) {
  const t = String(raw).trim();
  if (!t) return true;
  if (t === "-" || t === "—" || t === "–") return true;
  const u = t.toUpperCase();
  return [
    "N/A",
    "NA",
    "UNKNOWN",
    "NONE",
    "NULL",
    "..",
    ".",
    "UNDEFINED",
  ].includes(u);
}

/**
 * Parse published AL band 0–5 from `alignment_level`-style strings (`L0`…`L5`) or numeric.
 * Returns `null` when unpublished / sentinel (caller should show AL ?, not AL0).
 */
export const parseAlignmentLevel = (value, fallbackWhenUnparseable = 0) => {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return null;
    if (isUnpublishedAlignmentToken(trimmed)) return null;

    const normalized = trimmed.toUpperCase();
    const match = /^L([0-5])$/.exec(normalized);
    if (match) return Number(match[1]);
    const labelMatch = /^(?:ACCOUNT\s*LEVEL|AL)\s*-?\s*([0-5])\b/.exec(
      normalized
    );
    if (labelMatch) return Number(labelMatch[1]);
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return clampLevel(numeric, fallbackWhenUnparseable);
};

export const formatLevel = (prefix, value) => `${prefix} ${clampLevel(value)}`;

/** One-decimal display for server `rlAggregate` (e.g. 2.6 / 2.7); null when missing. */
export function formatAggregateRlValue(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(5, n)).toFixed(1);
}

/**
 * Missing AL/RL display: `AL ?` / `RL ?` (space before ?).
 * Other unknownLabel values also use a space (`AL -`).
 */
function formatUnknownLevelLabel(prefix, unknownLabel) {
  if (!prefix) return unknownLabel || "?";
  return `${prefix} ${unknownLabel || "?"}`;
}

/** Native tooltip / aria for unassessed AL ? / RL ? badges. */
export const UNASSESSED_LEVEL_TITLE = "Unassessed";

/** True when display text is AL ? / RL ? (optional space before ?). */
export function isUnassessedLevelLabel(label) {
  return /^(AL|RL)\s*\?$/i.test(String(label ?? "").trim());
}

/**
 * Hover title for unassessed level badges.
 * Pass a label (`AL ?`), `true`, or `"unknown"`.
 */
export function unassessedLevelTitle(labelOrFlag) {
  if (labelOrFlag === true || labelOrFlag === "unknown") {
    return UNASSESSED_LEVEL_TITLE;
  }
  if (isUnassessedLevelLabel(labelOrFlag)) return UNASSESSED_LEVEL_TITLE;
  return undefined;
}

/** Account aggregate RL label — do not use formatLevel (integer bands). */
export function formatAggregateRl(value, options = {}) {
  const { prefix = "RL", unknownLabel = "?" } = options;
  const display = formatAggregateRlValue(value);
  if (display === null) {
    return formatUnknownLevelLabel(prefix, unknownLabel);
  }
  return prefix ? `${prefix} ${display}` : display;
}

/**
 * Format published AL from customer_levels-style values (L3, 3, AL3).
 * Does not use assignment match_score (`alignment_level` on accounts APIs).
 */
export function formatPublishedAlignmentLevel(raw, options = {}) {
  const { unknownLabel = "?", prefix = "AL" } = options;
  const parsed = parseAlignmentLevel(raw);
  if (parsed === null || parsed === undefined) {
    return formatUnknownLevelLabel(prefix, unknownLabel);
  }
  return formatAlLevel(parsed, { unknownLabel, prefix });
}

/** Account AL label (integer level, or AL ? when missing). */
export function formatAlLevel(value, options = {}) {
  const { prefix = "AL", unknownLabel = "?" } = options;
  if (value === null || value === undefined || value === "") {
    return formatUnknownLevelLabel(prefix, unknownLabel);
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return formatUnknownLevelLabel(prefix, unknownLabel);
  }
  return prefix
    ? `${prefix} ${clampLevel(numeric)}`
    : String(clampLevel(numeric));
}

/** CSS class for AL badge (`al-0` … `al-5` or `al-unk`). */
export function getAlLevelBadgeClass(alLevel) {
  if (alLevel === null || alLevel === undefined || alLevel === "")
    return "al-unk";
  const numeric = Number(alLevel);
  if (!Number.isFinite(numeric)) return "al-unk";
  return `al-${clampLevel(numeric)}`;
}

export const formatPercent = value => `${Math.round(Number(value || 0))}%`;

/** Possible matrix cells = customer contacts × vendor stakeholders. */
export function computeMappingPossiblePairs(contactsCount, stakeholdersCount) {
  const contacts = Math.max(0, Number(contactsCount) || 0);
  const stakeholders = Math.max(0, Number(stakeholdersCount) || 0);
  return contacts * stakeholders;
}

/** Total possible vendor–contact pairs for an account (explicit field or contacts × stakeholders). */
export function resolveAccountMappingPossibleTotal(account) {
  const computed = computeMappingPossiblePairs(
    account?.contacts?.length,
    account?.stakeholders?.length
  );
  if (computed > 0) return computed;
  if (account?.mappingPossiblePairs != null) {
    return Math.max(0, Number(account.mappingPossiblePairs) || 0);
  }
  return 0;
}

/** Mapped vs possible pairs for portfolio / preview KPIs. */
export function getAccountMappingDisplay(account) {
  const mapped = Number(account?.mappedPairs) || 0;
  const possible = resolveAccountMappingPossibleTotal(account);
  return {
    mapped,
    possible,
    ratioLabel: `${mapped} / ${possible}`,
    subLabel: mapped === 1 ? "1 mapped pair" : `${mapped} mapped pairs`,
  };
}

/** Prototype Tab 2 coverage funnel: total possible → mapped → assessed. */
export function getAccountCoverageStats(account) {
  const total = resolveAccountMappingPossibleTotal(account);
  const mapped = Number(account?.mappedPairs) || 0;
  const assessed = Number(account?.assessedPairs) || 0;
  return {
    total,
    mapped,
    assessed,
    mappedPct: total ? Math.round((mapped / total) * 100) : 0,
    assessedPct: mapped ? Math.round((assessed / mapped) * 100) : 0,
    overallPct: total ? Math.round((assessed / total) * 100) : 0,
  };
}

/** Hover tooltip for Account Relationships picker cards. */
export function getAccountCoverageCardTooltip(account, cov = null) {
  const stats = cov || getAccountCoverageStats(account);
  const name = account?.name || "Account";
  const lines = [name];

  if (stats.total > 0) {
    lines.push(
      `Mapping: ${stats.mapped} of ${stats.total} possible vendor–contact pairs (${stats.mappedPct}% mapped)`
    );
    lines.push(
      `Assessed: ${stats.assessed} of ${stats.mapped} mapped pairs have RL scores (${stats.assessedPct}% assessed)`
    );
    lines.push(
      `Progress bar: light = ${stats.mappedPct}% mapped · solid = ${stats.overallPct}% assessed (of full matrix)`
    );
  } else if (stats.mapped > 0) {
    lines.push(`Mapping: ${stats.mapped} vendor–contact pairs linked`);
    lines.push(
      `Assessed: ${stats.assessed} of ${stats.mapped} mapped pairs have RL scores (${stats.assessedPct}% assessed)`
    );
    lines.push(
      "Progress bar: select this account to load the full matrix (contacts × exec team) and show mapping progress"
    );
  } else {
    lines.push("No mapping data for this account yet");
  }

  return lines.join("\n");
}

/** Personal coverage % for My Relationships cards (assessed / mapped on your pairs). */
export function getPersonalCoveragePercent(account) {
  const myPairs = Array.isArray(account?.myPairs) ? account.myPairs : null;
  const mapped = myPairs
    ? myPairs.length
    : Number(account?.myMappedPairs ?? account?.mappedPairs) || 0;
  const assessed = myPairs
    ? myPairs.filter(
        pair => pair.rlLevel !== null && pair.rlLevel !== undefined
      ).length
    : Number(account?.myAssessedPairs ?? account?.assessedPairs) || 0;
  if (!mapped) return 0;
  return Math.round((assessed / mapped) * 100);
}

/** Prototype Tab 3 card stats — ego-mapped pairs, RL band bar, You/Acct footer. */
export function getPersonalAccountCardStats(account, options = {}) {
  const youLabel = options.youLabel || "You";
  const myPairs = Array.isArray(account?.myPairs) ? account.myPairs : [];
  const hasPersonalPairs = Array.isArray(account?.myPairs);
  // Drop-1: when myPairs exist, You stats come from that list — not account totals.
  const assessedYou = myPairs.filter(
    pair => pair.rlLevel !== null && pair.rlLevel !== undefined
  );
  const mappedCt = hasPersonalPairs
    ? myPairs.length
    : Number(account?.myMappedPairs ?? account?.mappedPairs) || 0;
  const assessedYouCt = hasPersonalPairs
    ? assessedYou.length
    : Number(account?.myAssessedPairs ?? account?.assessedPairs) || 0;
  const strong = assessedYou.filter(pair => Number(pair.rlLevel) >= 4).length;
  const moderate = assessedYou.filter(pair => {
    const rl = Number(pair.rlLevel);
    return rl >= 2 && rl < 4;
  }).length;
  const weak = assessedYou.filter(pair => Number(pair.rlLevel) < 2).length;
  const allAccountContacts =
    (account?.contacts || []).length ||
    Number(account?.customerTeamMemberCount) ||
    0;
  const accountAssessedSet = new Set();
  // Prefer full team pairs when present; else personal pairs (partial Acct).
  const pairsForAcct =
    Array.isArray(account?.accountPairs) && account.accountPairs.length
      ? account.accountPairs
      : account?.pairs || [];
  pairsForAcct.forEach(pair => {
    if (pair.rlLevel !== null && pair.rlLevel !== undefined && pair.contactId) {
      accountAssessedSet.add(String(pair.contactId));
    }
  });
  const accountAssessedCt =
    account?.acctAssessedContacts != null
      ? Number(account.acctAssessedContacts) || 0
      : accountAssessedSet.size;
  const barTotal = mappedCt || 1;
  const pct = mappedCt > 0 ? Math.round((assessedYouCt / mappedCt) * 100) : 0;
  return {
    youLabel,
    mappedCt,
    assessedYouCt,
    barG: (strong / barTotal) * 100,
    barA: (moderate / barTotal) * 100,
    barR: (weak / barTotal) * 100,
    pct,
    accountAssessedCt,
    allAccountContacts,
    tooltipTitle: `${youLabel}: ${assessedYouCt}/${mappedCt} of your mapped contacts assessed · Acct: ${accountAssessedCt}/${allAccountContacts} contacts assessed by anyone on the team`,
  };
}

export const getAccountStatus = account => {
  if (!account) return "Unknown";
  if (account.pendingAssessments > 0) return "Needs RL assessment";
  if (account.alLevel === null || account.alLevel === undefined)
    return "No published AL";
  const delta = Number(account.rlAggregate || 0) - Number(account.alLevel);
  if (delta <= -2) return "RL risk";
  if (delta >= 2) return "Team outperforming";
  return "Aligned";
};

export const getRlBadgeClasses = level => {
  const palette = levelPalette[clampLevel(level)] || levelPalette[0];
  return `${palette.bg} ${palette.text} ${palette.border}`;
};

/** Client preview only — server GET /api/accounts/:id/rl-summary is authoritative. */
export const calculateRlAggregate = (pairs, aggregateSettings = {}) => {
  const items = (pairs || [])
    .filter(
      pair =>
        pair.rlLevel !== null &&
        pair.rlLevel !== undefined &&
        pair.isPrimaryOwner !== false
    )
    .map(pair => ({
      rlScore: clampLevel(pair.rlLevel),
      vendorGl: pair.ladderLevel,
      ladderLevel: pair.ladderLevel,
      meddpiccRole: pair.meddpiccRole,
      powerLevel: pair.powerLevel,
    }));
  return calculateHybridRlAggregate(items, aggregateSettings);
};

export const groupBy = (items, keyGetter) =>
  (items || []).reduce((acc, item) => {
    const key = keyGetter(item);
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

export const findPair = (pairs, stakeholderId, contact) => {
  const contactId = typeof contact === "object" ? contact?.id : contact;
  return (pairs || []).find(pair => {
    const idMatch =
      typeof contact === "object"
        ? pairMatchesContact(pair, contact)
        : String(pair.contactId) === String(contactId);
    return idMatch && pairMatchesStakeholder(pair, stakeholderId);
  });
};

/** First mapped pair in a matrix column (supports multiple contacts per MEDDPICC role). */
export function findPairForMatrixCell(pairs, stakeholderId, columnDef) {
  const contacts = columnDef?.contacts?.length
    ? columnDef.contacts
    : columnDef?.contact
      ? [columnDef.contact]
      : [];
  for (const contact of contacts) {
    const pair = (pairs || []).find(
      p =>
        pairMatchesContact(p, contact) &&
        pairMatchesStakeholder(p, stakeholderId)
    );
    if (pair) return { pair, contact };
  }
  return { pair: null, contact: contacts[0] || null };
}

function mergeContactsById(...lists) {
  const map = new Map();
  for (const list of lists) {
    for (const contact of list || []) {
      if (!contact?.id) continue;
      const prev = map.get(contact.id);
      map.set(
        contact.id,
        prev
          ? { ...prev, ...contact, raw: { ...prev.raw, ...contact.raw } }
          : contact
      );
    }
  }
  return [...map.values()];
}

/** Recompute pair vendor ids from raw (team row wins when both ids exist). */
export function normalizePairVendorFields(pair) {
  if (!pair) return pair;
  const raw = pair.raw || {};
  const mappingUserId = raw.mapping?.user_id || raw.mapping?.userId || null;
  const teamId = raw.account_team_member_id || raw.accountTeamMemberId || null;
  const execId = raw.executive_id || raw.executiveId || mappingUserId || null;
  const existingSourceId = pair.vendorSourceId
    ? String(pair.vendorSourceId)
    : "";
  const resolvedType =
    pair.vendorType || (teamId ? "team_member" : "executive");
  const sourceId =
    existingSourceId ||
    (teamId ? String(teamId) : execId ? String(execId) : "");
  return {
    ...pair,
    vendorType: resolvedType,
    vendorSourceId: sourceId || null,
    vendorStakeholderId:
      pair.vendorStakeholderId ||
      (sourceId
        ? `${resolvedType === "team_member" ? "team" : "exec"}-${sourceId}`
        : null),
  };
}

/** Org user id used by RL assessment/history APIs for a mapped pair. */
export function resolvePairAssessmentUserId(pair, stakeholder, executiveId) {
  const resolved =
    pair?.vendorSourceId ||
    pair?.raw?.mapping?.user_id ||
    pair?.raw?.mapping?.userId ||
    pair?.raw?.assessment?.user_id ||
    stakeholder?.sourceId ||
    executiveId ||
    null;
  return resolved ? String(resolved) : null;
}

/** Customer contact id used by RL assessment/history APIs for a mapped pair. */
export function resolvePairContactId(pair, contact) {
  const resolved =
    pair?.contactId ||
    contact?.id ||
    pair?.raw?.contact?.user_id ||
    pair?.raw?.contact?.id ||
    null;
  return resolved ? String(resolved) : null;
}

/** Merge hydrated/API overlay onto bulk-loaded account without losing team pair row keys. */
export function mergeAccountRelationshipBundle(base, overlay) {
  if (!overlay) return base;
  const contacts = mergeContactsById(base.contacts, overlay.contacts);
  const stakeholders = overlay.stakeholders?.length
    ? overlay.stakeholders
    : base.stakeholders;
  const baseById = new Map((base.pairs || []).map(p => [String(p.id), p]));
  const merged = [];
  for (const op of overlay.pairs || []) {
    const prev = baseById.get(String(op.id));
    if (prev) {
      baseById.delete(String(op.id));
      merged.push(
        normalizePairVendorFields({
          ...prev,
          ...op,
          rlLevel: op.rlLevel ?? prev.rlLevel,
          assessedPosture: op.assessedPosture ?? prev.assessedPosture,
          rationale: op.rationale ?? prev.rationale,
          assessmentAssignmentId:
            op.assessmentAssignmentId ?? prev.assessmentAssignmentId,
          assessedAt: op.assessedAt ?? prev.assessedAt,
          goalTargetRl: op.goalTargetRl ?? prev.goalTargetRl,
          goalTargetHorizon: op.goalTargetHorizon ?? prev.goalTargetHorizon,
          reassessBy: op.reassessBy ?? prev.reassessBy,
          raw: { ...prev.raw, ...op.raw },
        })
      );
    } else {
      merged.push(normalizePairVendorFields(op));
    }
  }
  if (overlay.source !== "api") {
    for (const p of baseById.values()) merged.push(p);
  }
  if (overlay.source === "api") {
    const byVendorContact = new Map();
    for (const pair of merged) {
      const normalized = normalizePairVendorFields(pair);
      const userId =
        normalized.vendorSourceId ||
        normalized.raw?.executive_id ||
        normalized.raw?.account_team_member_id;
      const contactId =
        normalized.raw?.mapping?.contact_id || normalized.contactId;
      const key =
        userId && contactId ? `${userId}::${contactId}` : String(normalized.id);
      const prev = byVendorContact.get(key);
      byVendorContact.set(
        key,
        prev
          ? normalizePairVendorFields({
              ...prev,
              ...normalized,
              rlLevel: normalized.rlLevel ?? prev.rlLevel,
            })
          : normalized
      );
    }
    merged.length = 0;
    merged.push(...byVendorContact.values());
  }
  const assessedPairs = merged.filter(
    p => p.rlLevel !== null && p.rlLevel !== undefined
  ).length;
  const coveredContacts = new Set(merged.map(p => p.contactId).filter(Boolean))
    .size;
  return {
    ...base,
    ...overlay,
    contacts,
    stakeholders,
    pairs: merged,
    mappedPairs: merged.length,
    mappingPossiblePairs: contacts.length * stakeholders.length,
    assessedPairs,
    pendingAssessments: Math.max(0, merged.length - assessedPairs),
    coveragePercent: contacts.length
      ? Math.round((coveredContacts / contacts.length) * 100)
      : 0,
  };
}

/** Prototype matrix RL pill (`rp-0` … `rp-5`); RL 4–5 use strong green (`rp-5`). */
export function getPrototypeRlPillClass(rl) {
  const level = clampLevel(rl);
  if (level >= STRONG_RL_MIN) return "rp-5";
  return `rp-${level}`;
}

/** Prototype `acct-bubble ab-al-*` classes (AL band coloring on portfolio grid). */
export const getPrototypeAlBubbleClass = al => {
  if (al === null || al === undefined) return "ab-al-unk";
  const numeric = Number(al);
  if (!Number.isFinite(numeric)) return "ab-al-unk";
  return `ab-al-${clampLevel(numeric)}`;
};

/**
 * Portfolio grid drift chevron on account bubbles.
 * Uses the fractional part within each RL band (0–5): same rule for every level.
 * - frac >= 0.6 → forward (e.g. 2.6, 2.7, 3.8)
 * - 0 < frac <= 0.4 → backward (e.g. 2.4, 2.3, 4.1)
 * - No icon at whole levels (1, 2, 3, …), midpoints (1.5, 2.5, …), or neutral band (0.4 < frac < 0.6)
 */
export function resolveRlAggregateDriftChevron(rlAggregate) {
  if (rlAggregate === null || rlAggregate === undefined) return null;
  const raw = Number(rlAggregate);
  if (!Number.isFinite(raw)) return null;

  const clamped = Math.max(0, Math.min(5, raw));
  const frac = Math.round((clamped - Math.floor(clamped)) * 100) / 100;

  if (frac >= 0.6) {
    const opacity = Math.min(1, 0.4 + ((frac - 0.6) / 0.4) * 0.6);
    return { direction: "forward", opacity };
  }
  if (frac > 0 && frac <= 0.4) {
    const opacity = Math.min(1, 0.4 + ((0.4 - frac) / 0.4) * 0.6);
    return { direction: "backward", opacity };
  }
  return null;
}
